/**
 * recon-pipeline.js — orchestrates the browser-side de-text reconstruction.
 *
 * v2 (etalon rebuild): all heavy math lives in recon-worker.js; this file owns
 * decode (VbrFrameSource / <video> seek), full-res compositing and encode
 * (CanvasVideoExport). Two passes:
 *
 *   ANALYZE  — sparse <video>-seek samples across the whole clip → the worker
 *              builds a Global Background Plate per region (recon-plate.js).
 *              Static shots get duty-cycle-independent background recovery;
 *              permanent overlays get a one-shot Telea patch; both can be
 *              upgraded once by an optional inpainting network (netFill). Moving
 *              shots refuse the plate and use the proven local-window path.
 *
 *   RENDER   — stream every frame at output res; proc-res copies go to the
 *              worker ring; the worker emits per-region fill patches (glyph
 *              mask ∧ plate / window median / permanent patch); the main
 *              thread upscales, composites, applies censor covers and feeds
 *              the hardware encoder through a back-pressured bridge.
 *
 * Also exposes previewAt(t): the SAME worker math on the current frame — the
 * preview the user sees is the algorithm that exports, not an imitation.
 *
 * Reuses (never reinvents): VbrFrameSource (decode), CanvasVideoExport
 * (encode), the RtX engine modules inside the worker. CSP-clean, no OpenCV.
 *
 * window.RtReconPipeline.createSession(opts) → session
 *   .setRegions(regions)            — regions incl. censor/protect/keyframes
 *   .analyze({onProgress})          — builds plates; resolves region kinds
 *   .previewAt(t, targetCtx, ow, oh) — honest single-frame preview
 *   .export(opts)                   — Promise<Blob>
 *   .dispose()
 * Back-compat: RtReconPipeline.run(opts) still works (analyze+export).
 */
(function (root) {
    'use strict';
    if (root.RtReconPipeline && root.RtReconPipeline.__v2) return;

    function makeCanvas(w, h) {
        var c = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h) : document.createElement('canvas');
        c.width = w; c.height = h; return c;
    }
    function ctxOf(c) { return c.getContext('2d', { willReadFrequently: true }); }

    /* Liveness heartbeat — the same `stall-tick` every other long lane on the
       site publishes (see canvas-video-export.js for why this lane needed one).
       Called on each real unit of work: an analyze sub-step, a worker reply, a
       frame handed to the encoder. Never called from a WAIT — a tick from a
       polling loop would tell the watchdog that a wedge is healthy. */
    function beat() { try { root.dispatchEvent && root.dispatchEvent(new CustomEvent('stall-tick')); } catch (_) {} }
    /* One threshold for the whole lane — owned by canvas-video-export.js so a
       second copy cannot drift away from the first. */
    function stallFloorMs() {
        var v = root.CanvasVideoExport && root.CanvasVideoExport.stallFloorMs;
        return (typeof v === 'number' && v > 0) ? v : 180000;
    }

    function makeBridge(capacity) {
        var buf = new Map(), waiters = new Map(), slot = [];
        return {
            put: function (i, v) {
                return new Promise(function (res) {
                    (function tryPut() {
                        if (buf.size < capacity) { buf.set(i, v); var w = waiters.get(i); if (w) { waiters.delete(i); w(); } res(); }
                        else slot.push(tryPut);
                    })();
                });
            },
            take: function (i) {
                return new Promise(function (res) {
                    if (buf.has(i)) { var v = buf.get(i); buf.delete(i); var s = slot.shift(); if (s) s(); res(v); }
                    else waiters.set(i, function () { var v = buf.get(i); buf.delete(i); var s = slot.shift(); if (s) s(); res(v); });
                });
            }
        };
    }

    function isCensor(method) { return method === 'blur' || method === 'pixelate' || method === 'solid'; }

    /* splitByShots(regions, shots, dur, fps) — a reconstruct region cut at the shot
       boundaries: one region per shot it covers (id `<id>@<shot>`, `parent`), each with
       its shot as the only place its background may come from (srcT0/srcT1). A region
       inside one shot keeps its id; one shot (or none) → the list comes back as is.
       Object mode keeps its own tracking; censor and protect hide by time per frame.
       A part under two frames (a transition shot) joins its neighbour. */
    function splitByShots(list, shots, dur, fps) {
        if (!shots || shots.length < 2) return list;
        var out = [], minLen = 2 / (fps || 30);
        list.forEach(function (r) {
            if (r.method !== 'reconstruct' || r.moving || r.forceWindow) { out.push(r); return; }
            var a = r.startTime || 0, b = r.endTime == null ? dur : r.endTime, parts = [];
            for (var i = 0; i < shots.length; i++) {
                var s0 = Math.max(a, shots[i].t0), s1 = Math.min(b, shots[i].t1);
                if (s1 > s0) parts.push({ i: i, s0: s0, s1: s1, sh: shots[i] });
            }
            // a sliver (a transition shot, a cut found a frame off) is covered by its
            // neighbour's part — dropped, it would let the caption flash for a frame
            for (var k = 0; k < parts.length && parts.length > 1; k++) {
                if (parts[k].s1 - parts[k].s0 >= minLen) continue;
                var nb = (k > 0) ? parts[k - 1] : parts[k + 1];
                nb.s0 = Math.min(nb.s0, parts[k].s0); nb.s1 = Math.max(nb.s1, parts[k].s1);
                parts.splice(k, 1); k--;
            }
            if (!parts.length) { out.push(r); return; }
            if (parts.length === 1) { out.push(Object.assign({}, r, { startTime: parts[0].s0, endTime: parts[0].s1, srcT0: parts[0].sh.t0, srcT1: parts[0].sh.t1 })); return; }
            parts.forEach(function (pt) {
                out.push(Object.assign({}, r, { id: r.id + '@' + pt.i, parent: r.id, startTime: pt.s0, endTime: pt.s1, srcT0: pt.sh.t0, srcT1: pt.sh.t1 }));
            });
        });
        return out;
    }

    function applyCensor(ctx, r, ow, oh, protects) {
        var x = Math.round(r.x * ow), y = Math.round(r.y * oh);
        var w = Math.max(1, Math.round(r.w * ow)), h = Math.max(1, Math.round(r.h * oh));
        ctx.save();
        // protect zones punch through censor covers too
        if (protects && protects.length) {
            ctx.beginPath();
            ctx.rect(0, 0, ow, oh);
            for (var i = 0; i < protects.length; i++) {
                var P = protects[i];
                ctx.rect(Math.round(P.x * ow), Math.round(P.y * oh), Math.round(P.w * ow), Math.round(P.h * oh));
            }
            try { ctx.clip('evenodd'); } catch (e) { }
        }
        if (r.method === 'solid') {
            ctx.fillStyle = r.color || '#000'; ctx.fillRect(x, y, w, h);
        } else if (r.method === 'pixelate') {
            var bs = Math.max(2, Math.round((r.intensity || 50) * 0.3));
            var sw = Math.max(1, Math.round(w / bs)), sh = Math.max(1, Math.round(h / bs));
            var tmp = makeCanvas(sw, sh), tc = ctxOf(tmp);
            tc.drawImage(ctx.canvas, x, y, w, h, 0, 0, sw, sh);
            ctx.imageSmoothingEnabled = false; ctx.drawImage(tmp, 0, 0, sw, sh, x, y, w, h);
        } else {
            var rad = Math.max(1, Math.round((r.intensity || 50) * 0.3));
            // Цензура размытием обязана СПРЯТАТЬ текст целиком. Прежнее
            // «размыть пиксели самой области» оставляло у её края 120 из 255
            // уровней исходника во ВСЕХ браузерах (пятно к краю становилось
            // прозрачным), а на Safari ≤ 17 не размывало ничего. blurHide
            // берёт поле настоящих соседей и заменяет область (27.09.2026).
            if (root.CanvasCssFilter && root.CanvasCssFilter.blurHide) {
                root.CanvasCssFilter.blurHide(ctx, null, x, y, w, h, rad);
            } else {
                try { ctx.filter = 'blur(' + rad + 'px)'; ctx.drawImage(ctx.canvas, x, y, w, h, x, y, w, h); } catch (e) { }
            }
        }
        ctx.restore();
    }

    function lumaFrom(d, n) {
        var out = new Float64Array(n);
        for (var i = 0, p = 0; i < n; i++, p += 4) out[i] = d[p] * 0.299 + d[p + 1] * 0.587 + d[p + 2] * 0.114;
        return out;
    }

    function regionSig(regions) {
        return JSON.stringify(regions.map(function (r) {
            // `moving`/`forceWindow` belong here too: flipping either changes
            // which tier runs, so a cached analysis from the other mode must
            // not be reused.
            return [r.id, r.method, r.kind, r.shape, r.x, r.y, r.w, r.h, r.startTime, r.endTime,
                r.keyframes || null, !!r.moving, !!r.forceWindow,
                // silhouette keyframes: count + span is enough to tell one track from another
                r.masks ? [r.masks.length, r.masks[0].t, r.masks[r.masks.length - 1].t] : null];
        }));
    }

    /* ── stroke core for permanent overlays (pure; exported for node tests) ──
       The user draws a generous rectangle, but the overlay itself is thin
       glyph strokes: synthesising the WHOLE rect throws away every real
       background pixel between the letters and reads as a smudged box — the
       exact "он просто размывает" class of complaint. DBNet's per-pixel
       probability map (already shipped for auto-detect, both shards) says
       where the strokes are; the fill core shrinks to them and the rest of
       the rect stays live footage. Guarded twice, both failures falling back
       to the proven full-rect fill (return null):
         · coverage sanity  — <0.5% (detector missed) or >65% (opaque plaque:
           the whole box IS overlay) → full rect;
         · evidence capture — the mask must contain ≥65% of the region's
           luma-extreme edge pixels (glyphScore's features); less means DBNet
           missed part of the text and stroke-filling would LEAVE TEXT ON
           SCREEN, which is worse than a soft fill. */
    function dilateBin1(src, w, h) {
        var out = new Uint8Array(src.length);
        for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
                var v = 0;
                for (var dy = -1; dy <= 1 && !v; dy++) {
                    var yy = y + dy; if (yy < 0 || yy >= h) continue;
                    for (var dx = -1; dx <= 1; dx++) {
                        var xx = x + dx; if (xx < 0 || xx >= w) continue;
                        if (src[yy * w + xx]) { v = 1; break; }
                    }
                }
                out[y * w + x] = v;
            }
        }
        return out;
    }
    function strokeCore(det, bbox, plate, pw, ph) {
        var n = bbox.w * bbox.h;
        var stroke = new Uint8Array(n), cov = 0;
        for (var y = 0; y < bbox.h; y++) {
            var sy = Math.min(det.dh - 1, Math.max(0, Math.round((bbox.y + y + 0.5) / ph * det.dh - 0.5)));
            for (var x = 0; x < bbox.w; x++) {
                var sx = Math.min(det.dw - 1, Math.max(0, Math.round((bbox.x + x + 0.5) / pw * det.dw - 0.5)));
                if (det.bin[sy * det.dw + sx]) { stroke[y * bbox.w + x] = 1; cov++; }
            }
        }
        if (cov / n < 0.005) return null;          // detector saw no text here
        // Union with the plate's own glyph evidence (luma-extreme ∧ strong
        // edge). DBNet marks the glyph BODY; the dark outline/glow around the
        // letters scores below its threshold, and a mask without the outline
        // both leaves ghost text on screen AND seeds the inpaint with the
        // outline's near-black pixels (measured: black smears instead of fill).
        for (var yy = 1; yy < bbox.h - 1; yy++) {
            for (var xx = 1; xx < bbox.w - 1; xx++) {
                var i = yy * bbox.w + xx, p = i * 4;
                if (stroke[i]) continue;
                var l = plate[p] * 0.299 + plate[p + 1] * 0.587 + plate[p + 2] * 0.114;
                if (Math.abs(l - 128) < 75) continue;
                var pl = (i - 1) * 4, pr = (i + 1) * 4, pu = (i - bbox.w) * 4, pd = (i + bbox.w) * 4;
                var ll = plate[pl] * 0.299 + plate[pl + 1] * 0.587 + plate[pl + 2] * 0.114;
                var lr = plate[pr] * 0.299 + plate[pr + 1] * 0.587 + plate[pr + 2] * 0.114;
                var lu = plate[pu] * 0.299 + plate[pu + 1] * 0.587 + plate[pu + 2] * 0.114;
                var ld = plate[pd] * 0.299 + plate[pd + 1] * 0.587 + plate[pd + 2] * 0.114;
                if (Math.max(Math.abs(lr - ll), Math.abs(ld - lu)) <= 35) continue;
                stroke[i] = 1; cov++;
            }
        }
        stroke = dilateBin1(dilateBin1(stroke, bbox.w, bbox.h), bbox.w, bbox.h);
        var frac = 0;
        for (var ci = 0; ci < n; ci++) if (stroke[ci]) frac++;
        frac /= n;
        // box entirely text (auto-detect draws TIGHT boxes — a healthy text
        // line with outline + dilation legitimately covers well over half)
        if (frac > 0.85) return null;
        // Plaque guard: an opaque/tinted caption box is FLAT between the
        // letters — stroke-filling it would erase the text but leave the box
        // on screen. Flat non-stroke pixels → full rect. (This also skips the
        // stroke path on genuinely flat backgrounds, where the full-rect fill
        // is already visually clean — nothing lost.)
        var sum = 0, sum2 = 0, m = 0;
        for (var fi = 0, fp = 0; fi < n; fi++, fp += 4) {
            if (stroke[fi]) continue;
            var fl = plate[fp] * 0.299 + plate[fp + 1] * 0.587 + plate[fp + 2] * 0.114;
            sum += fl; sum2 += fl * fl; m++;
        }
        if (m > 16) {
            var mean = sum / m, sd = Math.sqrt(Math.max(0, sum2 / m - mean * mean));
            if (sd < 6) return null;
        }
        return stroke;
    }

    /* Translucent-plaque guard (pure; exported for node tests). The flatness
       check above catches OPAQUE caption boxes, but a translucent box lets the
       background texture bleed through (sd ≥ 6) while tinting every pixel —
       stroke-filling would erase the letters and LEAVE THE BOX on screen
       (measured on real footage: 9.9 dB vs 14.3 full-rect). A tint is a
       systematic per-channel shift of the box's NON-stroke pixels against the
       real footage ring around the region. Both sides are sampled from the
       SAME frame — comparing the plate's temporal statistics against an
       instant ring false-trips on any live scene (measured: the caption band
       lost its stroke path because the plate's dominant mode was dark water
       while the mid-clip ring showed the bright jellyfish). */
    function plaqueTint(frame, bbox, stroke, pw, ph) {
        var pad = 8;
        var ring = [0, 0, 0], rn = 0, inn = [0, 0, 0], inN = 0;
        var x0 = Math.max(0, bbox.x - pad), y0 = Math.max(0, bbox.y - pad);
        var x1 = Math.min(pw, bbox.x + bbox.w + pad), y1 = Math.min(ph, bbox.y + bbox.h + pad);
        for (var y = y0; y < y1; y++) {
            for (var x = x0; x < x1; x++) {
                var q = (y * pw + x) * 4;
                var inside = x >= bbox.x && x < bbox.x + bbox.w && y >= bbox.y && y < bbox.y + bbox.h;
                if (inside) {
                    var i = (y - bbox.y) * bbox.w + (x - bbox.x);
                    if (stroke[i]) continue;
                    inn[0] += frame[q]; inn[1] += frame[q + 1]; inn[2] += frame[q + 2]; inN++;
                } else {
                    ring[0] += frame[q]; ring[1] += frame[q + 1]; ring[2] += frame[q + 2]; rn++;
                }
            }
        }
        if (!rn || !inN) return false;
        var d = Math.max(
            Math.abs(inn[0] / inN - ring[0] / rn),
            Math.abs(inn[1] / inN - ring[1] / rn),
            Math.abs(inn[2] / inN - ring[2] / rn));
        return d > 20;
    }

    /* detector hits inside a proc-space bbox, mapped to det-map coords */
    function detHitsIn(det, bbox, pw, ph) {
        var dx0 = Math.max(0, Math.floor(bbox.x / pw * det.dw)), dy0 = Math.max(0, Math.floor(bbox.y / ph * det.dh));
        var dx1 = Math.min(det.dw, Math.ceil((bbox.x + bbox.w) / pw * det.dw)), dy1 = Math.min(det.dh, Math.ceil((bbox.y + bbox.h) / ph * det.dh));
        var hits = 0;
        for (var y = dy0; y < dy1; y++)
            for (var x = dx0; x < dx1; x++)
                if (det.bin[y * det.dw + x]) hits++;
        return { hits: hits, area: Math.max(1, (dx1 - dx0) * (dy1 - dy0)) };
    }

    function createSession(opts) {
        var file = opts.file, url = opts.url;
        var vw = opts.width, vh = opts.height, dur = opts.duration;
        var fps = opts.fps || 30;
        /* The clip's own frames (VbrFrameSource.frameTimes): frame k of every ring,
           path and render below is the k-th picture of the FILE, shown at tOf(k).
           A phone in dim light or a screen recording changes its rate inside the
           file, and a grid at the average rate dropped its fast stretches and
           repeated its slow ones (#9567: 82 of 581 frames never reached the
           export). No times → the grid k/fps, exactly as before; `fps` stays the
           nominal rate the frame-count heuristics (rings, look-ahead) speak. */
        var TL = (opts.frameTimes && opts.frameTimes.length > 1 && root.VbrFrameSource && root.VbrFrameSource.timeline)
            ? root.VbrFrameSource.timeline(opts.frameTimes) : null;
        function tOf(k) { return TL ? TL.at(k) : k / fps; }
        function kAt(t) { return TL ? TL.onScreen(t) : Math.round(t * fps); }
        function lastK() { return TL ? TL.n - 1 : Infinity; }
        // where a <video> seek lands ON frame k: its middle (a seek onto its first
        // instant may show the picture before it)
        function seekTimeOf(k) {
            if (!TL) return k / fps;
            var a = TL.at(k), b = k + 1 < TL.n ? TL.at(k + 1) : a + 1 / fps;
            return a + 0.5 * (b - a);
        }
        var procMax = opts.procMaxEdge || 480;
        var scale = Math.min(1, procMax / Math.max(vw, vh));
        var pw = Math.max(16, Math.round(vw * scale)), ph = Math.max(16, Math.round(vh * scale));
        var netFill = opts.netFill || null;                 // async ({rgba,w,h,holes}) → Uint8ClampedArray|null (rt-netfill.js)
        var strokes = opts.strokes || null;                 // {atTime(t), fromRgba(rgba,w,h)} → {bin:Uint8Array, dw, dh}|null (DBNet per-pixel text map)
        var workerUrl = opts.workerUrl;
        var engineUrls = opts.engineUrls || [];
        /* v3 — the overlay-layer engine (ov-session.js). It takes the regions it
           can rebuild from real pixels of the clip at OUTPUT resolution (a caption
           over a moving camera — complaint #8478) and leaves the rest to the
           tiers below; ovIds names the regions it owns after analyze(). */
        var ovCfg = opts.ov || null;
        var ovEng = (ovCfg && root.RtOvEngine && root.RtOvCamera && root.RtOvLayer)
            ? root.RtOvEngine.create({
                file: file, url: url, vw: vw, vh: vh, fps: fps, dur: dur, frameTimes: TL ? TL.times : null,
                workerUrl: ovCfg.workerUrl, depUrls: ovCfg.depUrls || [],
                synth: ovCfg.synth || null, synthName: ovCfg.synthName || '',
                seg: ovCfg.seg || null, feat: ovCfg.feat || null, strokes: ovCfg.strokes || null
            })
            : null;
        var ovIds = {};
        function hasOv() { for (var k in ovIds) if (ovIds.hasOwnProperty(k)) return true; return false; }

        var worker = null, msgId = 0, pending = new Map(), emitWaiters = new Map();
        var regions = [], reconRegions = [], censorRegions = [], protectRegions = [];
        /* SHOTS (rt-shots.js). `userRegions` are the page's regions; `regions` — what
           the engines see — are the same regions cut at the shot boundaries: a caption
           region drawn over a montage becomes one region per shot it covers, each
           carrying its shot as the only place its background may come from
           (srcT0/srcT1). One shot → the regions pass unchanged, ids and all.
           Complaint #9567: a 34-s promo of twenty shots, the closing card's plate a
           median of the theatre, the hotel and the crowd. */
        var userRegions = [], shots = opts.shots || null, shotsProm = null;
        function ensureShots(signal, onProg) {
            if (shots) return Promise.resolve(shots);
            if (shotsProm) return shotsProm;
            var S0 = root.RtShots;
            if (!S0 || !S0.scan || !root.VbrFrameSource || !file) { shots = [{ t0: 0, t1: dur }]; return Promise.resolve(shots); }
            shotsProm = S0.scan({
                file: file, url: url, duration: dur, width: vw, height: vh, fps: Math.min(30, fps), signal: signal, times: TL ? TL.times : null,
                // a long clip scans for a minute: the bar moves and the stall watch hears it
                onProgress: function (f) { beat(); if (onProg) onProg(f); }
            })
                .then(function (sh) { shots = (sh && sh.length) ? sh : [{ t0: 0, t1: dur }]; return shots; }, function (e) {
                    shotsProm = null;
                    if ((signal && signal.aborted) || (e && e.name === 'AbortError')) throw e;
                    // no cut list is a single shot — the engines run as they always did
                    try { root.__aeFeature && root.__aeFeature('rt_shots_fail', { why: String((e && e.message) || e).slice(0, 60) }); } catch (_) { }
                    shots = [{ t0: 0, t1: dur }];
                    return shots;
                });
            return shotsProm;
        }
        function applyRegions() {
            regions = splitByShots(userRegions, shots, dur, fps);
            reconRegions = regions.filter(function (r) { return r.method === 'reconstruct'; });
            censorRegions = regions.filter(function (r) { return isCensor(r.method); });
            protectRegions = regions.filter(function (r) { return r.method === 'protect'; });
        }
        /* what the page reads: meta under ITS region ids — a region cut into shots is
           reported by its part the layer engine took, else by its longest part */
        function metaOut() {
            var out = new Map(), best = {};
            regions.forEach(function (r) {
                var m = regionMeta.get(r.id);
                if (!m) return;
                if (!r.parent) { out.set(r.id, m); return; }
                out.set(r.id, m);
                var len = (r.endTime || 0) - (r.startTime || 0), b = best[r.parent];
                if (!b || (m.layer && !b.m.layer) || (!!m.layer === !!b.m.layer && len > b.len)) best[r.parent] = { m: m, len: len };
            });
            for (var pid in best) if (best.hasOwnProperty(pid)) out.set(pid, best[pid].m);
            return out;
        }
        var lastCoverage = null;      // {ratio, regions:[{regionId, ratio, ...}]}
        var analyzed = false, analyzedSig = null;
        // Long-reach sources for object regions: sparse proc-res frames across
        // the whole clip, handed in by the page (setSamples) and re-sent to the
        // worker after every init — the worker is rebuilt per region set.
        var objectSamples = null;
        function sendSamples() {
            if (!objectSamples || !objectSamples.length) return;
            // structured clone: the page keeps its copy for the next session
            fire({ type: 'plateSamples', w: pw, h: ph, frames: objectSamples.map(function (f) { return { rgba: f.rgba, t: f.t }; }) });
        }
        var regionMeta = new Map();                         // id → {kind, usable, lowFrac, …}
        var seekVideo = null, seekCanvas = null, seekCtx = null;

        function ensureWorker() {
            if (worker) return worker;
            worker = new Worker(workerUrl);
            worker.onmessage = function (ev) {
                var m = ev.data;
                if (m.type === 'synthRequest') {
                    // A GAN keyframe: the worker hands over the frame as filled so
                    // far and the fresh holes; the page runs the hook (RU shard
                    // only) and answers with the completed frame, or null → the
                    // worker keeps Telea. Never throws into the worker's wait.
                    var job = { rgba: new Uint8ClampedArray(m.rgba), w: m.w, h: m.h, holes: new Uint8Array(m.holes) };
                    Promise.resolve().then(function () { return netFill ? netFill(job) : null; }).then(function (res) {
                        beat();
                        if (res) { var rr = new Uint8ClampedArray(res); fire({ type: 'synthReply', key: m.key, rgba: rr.buffer }, [rr.buffer]); }
                        else fire({ type: 'synthReply', key: m.key, rgba: null });
                    }).catch(function () { fire({ type: 'synthReply', key: m.key, rgba: null }); });
                    return;
                }
                if (m.type === 'emitted') {
                    // Coverage rides along with every emitted frame: by the end
                    // of a render it is the honest answer to "how much of this
                    // came out of your own footage, and how much did we draw?".
                    if (m.coverage) lastCoverage = m.coverage;
                    var w = emitWaiters.get(m.c);
                    if (w) { emitWaiters.delete(m.c); beat(); w(m.patches); }
                    return;
                }
                if (m.type === 'error' && m.c != null && emitWaiters.has(m.c)) {
                    // An emit that threw inside the worker used to answer nobody:
                    // the waiter for that frame stayed pending and the render —
                    // probe, preview or export — hung forever with no error
                    // (found 17.09.2026 by a ReferenceError that showed as a
                    // 240-s silence). Adopting a rejected promise turns it into
                    // the throw the caller's guards already handle.
                    var ew = emitWaiters.get(m.c); emitWaiters.delete(m.c);
                    ew(Promise.reject(new Error('recon-worker emit: ' + (m.message || 'failed'))));
                    return;
                }
                if (m.id != null && pending.has(m.id)) {
                    var res = pending.get(m.id); pending.delete(m.id);
                    beat();                       // a worker reply is real work
                    if (m.type === 'error') res.reject(new Error(m.message));
                    else res.resolve(m);
                }
            };
            worker.onerror = function (e) {
                pending.forEach(function (res) { res.reject(new Error('worker error: ' + (e.message || 'unknown'))); });
                pending.clear();
            };
            return worker;
        }
        function call(msg, transfer) {
            ensureWorker();
            return new Promise(function (resolve, reject) {
                msg.id = ++msgId;
                pending.set(msg.id, { resolve: resolve, reject: reject });
                worker.postMessage(msg, transfer || []);
            });
        }
        function fire(msg, transfer) { ensureWorker(); worker.postMessage(msg, transfer || []); }

        /* The analysis, the preview's sparse ring and every per-frame fallback
           read this element. It has to be IN the document: iOS parks a detached
           media element (readyState 1, no `loadeddata`, no error), and a promise
           waiting on that event never settled — the preview hung, and the export
           queued behind it with it (7 phone sessions «processing» with no render,
           14.09.2026). The shared host lives in frame-source.js; the deadline turns
           a parked element into an error the caller can report. */
        var seekVideoPending = null;
        function ensureSeekVideo() {
            if (seekVideo) return Promise.resolve(seekVideo);
            if (seekVideoPending) return seekVideoPending;
            var v = document.createElement('video');
            v.muted = true; v.playsInline = true; v.preload = 'auto';
            var VFS = root.VbrFrameSource;
            if (VFS && VFS.attachHidden) VFS.attachHidden(v);
            v.src = url || URL.createObjectURL(file);
            seekVideoPending = new Promise(function (resolve, reject) {
                var settled = false;
                var onTimer = function () { fail('seek video failed: load-timeout'); };
                var timer = root.__visibleTimeout ? root.__visibleTimeout(onTimer, 20000) : setTimeout(onTimer, 20000);
                function stopTimer() { if (typeof timer === 'function') timer(); else clearTimeout(timer); }   // __visibleTimeout hands back its cancel
                function fail(m) {
                    if (settled) return; settled = true; stopTimer();
                    seekVideoPending = null;
                    try { if (!url && v.src) URL.revokeObjectURL(v.src); } catch (_) { }
                    try { v.removeAttribute('src'); v.load(); } catch (_) { }
                    if (VFS && VFS.detachHidden) VFS.detachHidden(v);
                    reject(new Error(m));
                }
                v.addEventListener('loadeddata', function () {
                    if (settled) return; settled = true; stopTimer();
                    seekVideo = v; seekVideoPending = null; resolve(v);
                }, { once: true });
                v.addEventListener('error', function () { fail('seek video failed'); }, { once: true });
            });
            return seekVideoPending;
        }
        function seekTo(v, t) {
            if (root.__seekPresented) return root.__seekPresented(v, t, 1800);
            return new Promise(function (resolve) {
                var done = function () { v.onseeked = null; resolve(); };
                v.onseeked = done;
                try { v.currentTime = t; } catch (e) { done(); }
                setTimeout(done, 1800);
            });
        }
        /* The hidden element that never loads. Since 02.10.2026 analysis on iOS
           runs RIGHT AFTER the v3 engine has decoded the whole clip through
           WebCodecs, and there the element created next gets no `loadeddata` at
           all: «seek video failed: load-timeout» went from ~3 a day to 23–34
           (RU, 03–07.10; Safari, Yandex and Chrome on iPhone; 78 errors in 56
           sessions against 13 in 10 the week before), and the export or the
           preview failed with it. The decoder lane itself works on the same
           phones — the v3 pass that just ran is that lane. So when the element
           cannot load, the frame comes from that lane: VbrFrameSource reads the
           one frame at t at SOURCE size onto a canvas that stands in for the
           element — the same size, so every drawImage(seekVideo, …) of the
           analysis reads it unchanged. noSeekFallback: the lane must not fall
           back to a second element, the very thing that failed. */
        var seekByDecoder = false, seekSurfaceCtx = null;
        function decoderFloorAvailable() {
            return !!(root.VbrFrameSource && root.VbrFrameSource.iterate && file && typeof VideoDecoder !== 'undefined');
        }
        async function decoderFrameAt(t) {
            if (!seekSurfaceCtx) { seekVideo = makeCanvas(vw, vh); seekSurfaceCtx = ctxOf(seekVideo); }
            var got = false;
            await root.VbrFrameSource.iterate({
                file: file, url: url, fps: fps, trimStart: t, trimEnd: Math.min(dur, t + 1 / fps),
                width: vw, height: vh, noSeekFallback: true
            }, function (canvas) {
                if (got) return;
                got = true;
                seekSurfaceCtx.clearRect(0, 0, vw, vh);
                seekSurfaceCtx.drawImage(canvas, 0, 0, vw, vh);
            });
            if (!got) throw new Error('seek video failed: decoder-empty');
            return seekVideo;
        }
        /* «The source frame at t, drawable at source size» — the one door every
           reader of seekVideo goes through. */
        function frameSurfaceAt(t) {
            var tt = Math.min(Math.max(0, t), Math.max(0, dur - 0.05));
            if (seekByDecoder) return decoderFrameAt(tt);
            return ensureSeekVideo().then(function (v) {
                return seekTo(v, tt).then(function () { return v; });
            }, function (e) {
                if (!decoderFloorAvailable()) throw e;
                seekByDecoder = true;
                try { root.__aeFeature && root.__aeFeature('rt_seek_decoder_floor', { why: String((e && e.message) || e).slice(0, 60), w: vw, h: vh, dur: Math.round(dur) }); } catch (_) {}
                return decoderFrameAt(tt);
            });
        }
        function procFrameAt(t) {
            return frameSurfaceAt(t).then(function (v) {
                if (!seekCanvas) { seekCanvas = makeCanvas(pw, ph); seekCtx = ctxOf(seekCanvas); }
                seekCtx.drawImage(v, 0, 0, pw, ph);
                return seekCtx.getImageData(0, 0, pw, ph).data;
            });
        }

        function bboxProcOf(r) {
            var x = Math.max(0, Math.floor(r.x * pw)), y = Math.max(0, Math.floor(r.y * ph));
            var x2 = Math.min(pw, Math.ceil((r.x + r.w) * pw)), y2 = Math.min(ph, Math.ceil((r.y + r.h) * ph));
            return { x: x, y: y, w: Math.max(1, x2 - x), h: Math.max(1, y2 - y) };
        }
        function sliceBbox(data, bbox) {
            var out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
            for (var row = 0; row < bbox.h; row++) {
                var src = ((bbox.y + row) * pw + bbox.x) * 4;
                out.set(data.subarray(src, src + bbox.w * 4), row * bbox.w * 4);
            }
            return out;
        }

        function setRegions(next) {
            userRegions = next || [];
            applyRegions();
            if (regionSig(userRegions) !== analyzedSig) analyzed = false;
        }

        function coreMask(r, bbox) {
            var core = new Uint8Array(bbox.w * bbox.h);
            var mm = root.RtMaskBuilder ? root.RtMaskBuilder.build(
                [{ id: r.id, shape: r.shape, x: r.x, y: r.y, w: r.w, h: r.h, points: r.points, startTime: 0, endTime: Infinity }],
                0, pw, ph, { featherPx: 0, dilatePx: 1 }) : null;
            if (mm) {
                for (var y = 0; y < bbox.h; y++)
                    for (var x = 0; x < bbox.w; x++)
                        core[y * bbox.w + x] = mm.core[(bbox.y + y) * pw + bbox.x + x] ? 1 : 0;
            } else core.fill(1);
            return core;
        }

        /* Permanent-overlay job: the region's plate PLUS a ring of real footage
           around it.
           Telea marches inward from known pixels, so a buffer that is entirely
           hole cannot be filled — and until this existed the tier passed exactly
           that (slice == bbox == core) and silently returned the text untouched.
           Measured on the reference clip: with no ring the subtitle band kept
           all 1894 of its bright pixels (PSNR 9.2, i.e. the input); a ring of
           just 2 px drops it to 0 (PSNR 20.4). RING is set to the inpaint
           radius so the weighting window always has real pixels to average. */
        var PERM_RING = 8;

        /* The moment a region is judged and patched at: the middle of ITS span. It
           was the middle of the CLIP — on a montage a frame of another scene (the
           closing card of #9567 was patched from the theatre at 17 s), and on any
           clip a caption shown for a while was patched from a frame without it.
           A region over the whole clip lands where it always did. */
        function midT(r) {
            var a = r.startTime || 0, b = r.endTime == null ? dur : Math.min(dur, r.endTime);
            return Math.max(0, Math.min(dur - 0.05, (a + b) / 2));
        }
        /* one DBNet stroke-map fetch per moment per analyze, shared by its permanent regions */
        var strokesProm = new Map();
        function strokesDet(t) {
            if (!strokes || !strokes.atTime) return Promise.resolve(null);
            var key = Math.round((t == null ? dur / 2 : t) * 10) / 10;
            if (!strokesProm.has(key)) {
                strokesProm.set(key, Promise.resolve()
                    .then(function () { return strokes.atTime(key); })
                    .catch(function () { return null; }));
            }
            return strokesProm.get(key);
        }

        async function buildPermanentJob(r, bbox, reply, strokeSmall) {
            var frame = await procFrameAt(midT(r));
            var x0 = Math.max(0, bbox.x - PERM_RING), y0 = Math.max(0, bbox.y - PERM_RING);
            var x1 = Math.min(pw, bbox.x + bbox.w + PERM_RING), y1 = Math.min(ph, bbox.y + bbox.h + PERM_RING);
            var bw = Math.max(1, x1 - x0), bh = Math.max(1, y1 - y0);

            var rgba = new Uint8ClampedArray(bw * bh * 4);
            for (var y = 0; y < bh; y++) {
                var src = ((y0 + y) * pw + x0) * 4;
                rgba.set(frame.subarray(src, src + bw * 4), y * bw * 4);
            }
            // the plate (median appearance over the clip) is a cleaner starting
            // point inside the region than any single frame
            var plate = new Uint8ClampedArray(reply.plate);
            var pwid = reply.w || bbox.w, phei = reply.h || bbox.h;
            for (var py = 0; py < phei && py < bbox.h; py++) {
                var d = ((bbox.y - y0 + py) * bw + (bbox.x - x0)) * 4;
                var s = py * pwid * 4;
                rgba.set(plate.subarray(s, s + Math.min(pwid, bbox.w) * 4), d);
            }

            var small = coreMask(r, bbox);
            if (strokeSmall) {
                for (var si = 0; si < small.length; si++) if (!strokeSmall[si]) small[si] = 0;
            }
            var core = new Uint8Array(bw * bh);
            for (var cy = 0; cy < bbox.h; cy++)
                for (var cx = 0; cx < bbox.w; cx++)
                    if (small[cy * bbox.w + cx]) core[(bbox.y - y0 + cy) * bw + (bbox.x - x0) + cx] = 1;

            var coreCopy = new Uint8Array(core);   // core.buffer is transferred away
            return {
                x: x0, y: y0, w: bw, h: bh, rgba: rgba, core: core,
                coreForNet: function () { return coreCopy; }
            };
        }

        function normalizeRegion(r) {
            return {
                id: r.id, method: r.method, kind: r.kind || 'auto', shape: r.shape,
                x: r.x, y: r.y, w: r.w, h: r.h, points: r.points || null,
                keyframes: r.keyframes || null, startTime: r.startTime || 0,
                // silhouette keyframes from the tracker ({t,x,y,w,h,mw,mh,rle});
                // dropped here = every hole silently becomes a rectangle again
                masks: r.masks || null,
                endTime: r.endTime == null ? dur : r.endTime,
                // Object mode. `moving` tells the worker to mask each NEIGHBOUR
                // frame at that neighbour's own time instead of reusing the
                // target's mask, and `forceWindow` keeps it on the
                // motion-compensated local-window tier rather than the plate
                // (a plate assumes a screen-fixed overlay). This whitelist is
                // the only thing standing between the two modes — dropping
                // these two flags silently turns object removal back into a
                // no-op, which is exactly what it measured as: 13.05 dB
                // against an untouched input of 13.05 dB.
                moving: !!r.moving, forceWindow: !!r.forceWindow,
                // the shot this region lives in: where its background may come from
                srcT0: r.srcT0 == null ? 0 : r.srcT0, srcT1: r.srcT1 == null ? dur : r.srcT1
            };
        }

        /* ── ANALYZE ── */
        async function analyze(aopts) {
            aopts = aopts || {};
            var onProgress = aopts.onProgress || function () { };
            if (analyzed && regionSig(userRegions) === analyzedSig) return metaOut();
            // the cut list first (one decode of the clip, shared with the detector):
            // every estimator below reads one shot at a time
            if (userRegions.some(function (r) { return r.method === 'reconstruct' && !r.moving && !r.forceWindow; })) {
                if (!shots) {
                    // the scan (when the detector has not run it) takes the first tenth of the bar
                    await ensureShots(aopts.signal, function (f) { onProgress(0.1 * f); });
                    onProgress = (function (orig) { return function (f) { orig(0.1 + 0.9 * f); }; })(onProgress);
                }
                applyRegions();
            }
            // ── v3 first: the overlay-layer engine claims what it can rebuild ──
            ovIds = {};
            var ovPlans = null;
            var objectMode = reconRegions.length && reconRegions.every(function (r) { return r.moving; });
            if (ovEng && !objectMode && reconRegions.length) {
                ovPlans = await ovEng.analyze(reconRegions.map(normalizeRegion), {
                    onProgress: function (f) { onProgress(0.6 * f); }, signal: aopts.signal, shots: shots
                });
                ovPlans.forEach(function (p, id) { if (p.verdict === 'layer') ovIds[id] = 1; });
                // the tiers below own the remaining 40 % of the bar
                onProgress = (function (orig) { return function (f) { orig(0.6 + 0.4 * f); }; })(onProgress);
            }
            var v2Regions = regions.filter(function (r) { return !ovIds[r.id]; });
            var v2Recon = reconRegions.filter(function (r) { return !ovIds[r.id]; });
            // fresh worker state for a fresh region set
            if (worker) { worker.terminate(); worker = null; pending.clear(); emitWaiters.clear(); }
            await call({
                type: 'init', urls: engineUrls, pw: pw, ph: ph, fps: fps, times: TL ? TL.times : null,
                window: (vw * vh > 1920 * 1080 ? 8 : 12),
                regions: v2Regions.map(normalizeRegion)
            });
            regionMeta.clear();
            if (ovPlans) ovPlans.forEach(function (p, id) {
                if (p.verdict === 'layer') regionMeta.set(id, { kind: 'layer', layer: true, stats: p.stats, refined: p.refined || 0, ms: p.ms });
            });

            // ── object mode: nothing here applies ────────────────────────────
            // Everything below this point is text analysis — plate building,
            // glyph extraction, DBNet detection, translucent unblending. An
            // object is not a glyph: it is opaque, it is a blob rather than a
            // thin stroke, and it moves. Running that analysis over it costs
            // ~20 s of seeking to produce meta that the window tier ignores.
            // Mixed region sets still take the full path.
            if (objectMode) {
                for (var oi = 0; oi < reconRegions.length; oi++) {
                    var orr = reconRegions[oi];
                    fire({ type: 'setRegionMeta', regionId: orr.id, forceWindow: true });
                    regionMeta.set(orr.id, { kind: 'object', window: true });
                }
                // GAN cadence for fresh holes: one inference per ~window of
                // frames on WebGPU, a third as often on WASM; the memory tier
                // carries each result to its neighbours. 0 = Telea only.
                if (netFill) fire({ type: 'setSynth', every: opts.synthEvery || (vw * vh > 1920 * 1080 ? 8 : 12) });
                // the frames that show the background the object stands on
                sendSamples();
                analyzed = true;
                analyzedSig = regionSig(userRegions);
                onProgress(1);
                return metaOut();
            }

            // network fill jobs are built over a FULL proc frame with the best-known
            // bbox content pasted in — the GAN needs real square context
            // around the holes; a bare subtitle strip stretched to 512×512
            // produces banding garbage (found the hard way).
            async function netOverFrame(bboxJob, contentRgba, holesBbox, atT) {
                var frame = new Uint8ClampedArray(await procFrameAt(atT == null ? dur / 2 : atT));
                for (var row = 0; row < bboxJob.h; row++) {
                    var dst = ((bboxJob.y + row) * pw + bboxJob.x) * 4;
                    var src = row * bboxJob.w * 4;
                    frame.set(contentRgba.subarray(src, src + bboxJob.w * 4), dst);
                }
                var holesFull = new Uint8Array(pw * ph);
                for (var hy = 0; hy < bboxJob.h; hy++)
                    for (var hx = 0; hx < bboxJob.w; hx++)
                        if (holesBbox[hy * bboxJob.w + hx]) holesFull[(bboxJob.y + hy) * pw + bboxJob.x + hx] = 1;
                var up = await netFill({ rgba: frame, w: pw, h: ph, holes: holesFull });
                if (!up) return null;
                return sliceBbox(up, bboxJob);
            }
            // stroke-core fills queued for the post-fill detector verification
            var pendingVerify = [];

            var plateTargets = v2Recon.filter(function (r) { return !(r.keyframes && r.keyframes.length); });
            if (plateTargets.length) {
                // warm the stroke detector (own worker + video element) in
                // parallel with the sampling loop — its ~10-15 s ORT init would
                // otherwise serialize after the ~30 s of seeks
                plateTargets.forEach(function (r) { strokesDet(midT(r)); });
                // sparse samples across each region's SHOT (one decode serves all the
                // regions of a shot; a clip that is one shot samples it as before)
                var groups = [], gByKey = {};
                plateTargets.forEach(function (r) {
                    var a = r.srcT0 == null ? 0 : r.srcT0, b = r.srcT1 == null ? dur : Math.min(dur, r.srcT1), key = a + '|' + b;
                    if (!gByKey[key]) { gByKey[key] = { a: a, b: b, rs: [] }; groups.push(gByKey[key]); }
                    gByKey[key].rs.push(r);
                });
                groups.forEach(function (g) { g.M = Math.min(50, Math.max(16, Math.round((g.b - g.a) * 5))); });
                var Mtot = groups.reduce(function (acc, g) { return acc + g.M; }, 0), Mdone = 0;
                var perRegion = new Map(); plateTargets.forEach(function (r) { perRegion.set(r.id, []); });
                // Padded NATIVE-res slices feed the translucent-tier α
                // estimation: the estimator needs overlay-free ring pixels
                // around the text (a tight auto-detect box has almost none
                // inside it), and at proc res the α map is too coarse to pass
                // the detector verify (measured). The crops reuse the frames
                // the sampling loop already decoded — near-free.
                var padBoxes = new Map(), padUs = new Map(), perRegionPad = new Map();
                plateTargets.forEach(function (r) {
                    var b = bboxProcOf(r);
                    var padX = Math.max(12, Math.round(b.w * 0.35));
                    var padY = Math.max(12, Math.round(b.h * 0.6));
                    var x0 = Math.max(0, b.x - padX), y0 = Math.max(0, b.y - padY);
                    var x1 = Math.min(pw, b.x + b.w + padX), y1 = Math.min(ph, b.y + b.h + padY);
                    var pb = { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
                    padBoxes.set(r.id, pb);
                    // native rect of the padded box, capped to a 640 work edge
                    var nx = Math.round(pb.x / pw * vw), ny = Math.round(pb.y / ph * vh);
                    var nw = Math.max(1, Math.round(pb.w / pw * vw)), nh = Math.max(1, Math.round(pb.h / ph * vh));
                    var us = Math.min(1, 640 / Math.max(nw, nh));
                    var pu = {
                        nx: nx, ny: ny, nw: nw, nh: nh,
                        uw: Math.max(16, Math.round(nw * us)), uh: Math.max(16, Math.round(nh * us))
                    };
                    pu.cnv = makeCanvas(pu.uw, pu.uh);
                    pu.ctx = ctxOf(pu.cnv);
                    // the user's region mapped into the u-grid (α outside it is discarded)
                    var ib = b;
                    pu.inner = {
                        x: Math.round((ib.x / pw * vw - nx) * pu.uw / nw),
                        y: Math.round((ib.y / ph * vh - ny) * pu.uh / nh),
                        w: Math.round(ib.w / pw * vw * pu.uw / nw),
                        h: Math.round(ib.h / ph * vh * pu.uh / nh)
                    };
                    padUs.set(r.id, pu);
                    perRegionPad.set(r.id, []);
                });
                for (var gi = 0; gi < groups.length; gi++) {
                    var G = groups[gi];
                    for (var s = 0; s < G.M; s++) {
                        if (aopts.signal && aopts.signal.aborted) throw new DOMException('aborted', 'AbortError');
                        var t = G.a + (G.b - G.a) * ((s + 0.5) / G.M);
                        var data = await procFrameAt(t);
                        for (var pi = 0; pi < G.rs.length; pi++) {
                            var pr0 = G.rs[pi];
                            perRegion.get(pr0.id).push(sliceBbox(data, bboxProcOf(pr0)));
                            var pu0 = padUs.get(pr0.id);
                            pu0.ctx.drawImage(seekVideo, pu0.nx, pu0.ny, pu0.nw, pu0.nh, 0, 0, pu0.uw, pu0.uh);
                            perRegionPad.get(pr0.id).push(new Uint8ClampedArray(pu0.ctx.getImageData(0, 0, pu0.uw, pu0.uh).data));
                        }
                        beat();
                        onProgress(0.7 * (++Mdone) / Mtot);
                    }
                }
                /* ── the plate phase must SHOW that it is working ─────────────
                   Measured 2026-09-03, headless Chromium, 20 s SD clip, ONE
                   region: the bar sat motionless on 8.4% for 10.0 s — 41% of a
                   24.5 s export — with no ETA (the ETA is gated on f > 0.14 and
                   analyze tops out at 0.12). In Firefox the same stretch was
                   31.5 s. Cause: the loop below reported progress ONCE PER
                   REGION, at the end, and the overwhelmingly common case is one
                   region — so plateBuild + ghost check + network fill + unblend +
                   permanentPatch + a second network fill were a single motionless
                   step. Nothing was wrong; the tool simply looked dead, which
                   is the whole complaint ("экспорт зависает").

                   `sub()` walks a region's slice through its named sub-steps.
                   Monotonic by construction: a branch that skips steps jumps
                   forward, never back, and the slice never exceeds the region's
                   own share. It also beats, so a slow-but-alive analyze is
                   never mistaken for a wedge. */
                var SUBSTEPS = 9;
                var subDone = 0;
                var sub = function (regionIdx) {
                    subDone = Math.min(SUBSTEPS, subDone + 1);
                    var share = 0.3 / plateTargets.length;
                    beat();
                    onProgress(0.7 + share * (regionIdx + subDone / SUBSTEPS));
                };
                for (var i = 0; i < plateTargets.length; i++) {
                    subDone = 0;
                    var r = plateTargets[i];
                    var slices = perRegion.get(r.id);
                    var reply = await call({
                        type: 'plateBuild', regionId: r.id, bbox: bboxProcOf(r),
                        slices: slices.map(function (sl) { return sl.buffer; })
                    }, slices.map(function (sl) { return sl.buffer; }));
                    sub(i);                                        // plate built
                    var kind = r.kind && r.kind !== 'auto' ? r.kind : (reply.anchors > 0 ? 'intermittent' : 'permanent');
                    var meta = { kind: kind, usable: reply.usable, lowFrac: reply.lowFrac, anchors: reply.anchors };
                    // Ghost-plate check: an always-on overlay can sneak into the
                    // anchor plate — low-contrast text scores "quiet" whenever it
                    // sits over bright content, the region classifies intermittent,
                    // differs(cur, plate) ≈ 0 and the text silently STAYS on
                    // screen (measured on real footage: yellow no-outline caption
                    // survived untouched). Ask the detector whether the PLATE
                    // ITSELF contains text; contaminated → it is a permanent
                    // overlay and the permanent tier must handle it.
                    if (kind === 'intermittent' && reply.usable && strokes && strokes.fromRgba) {
                        try {
                            var gBbox = bboxProcOf(r);
                            var gFrame = new Uint8ClampedArray(await procFrameAt(midT(r)));
                            var gPlate = new Uint8ClampedArray(reply.plate);
                            for (var gr = 0; gr < gBbox.h; gr++) {
                                gFrame.set(gPlate.subarray(gr * gBbox.w * 4, (gr + 1) * gBbox.w * 4), ((gBbox.y + gr) * pw + gBbox.x) * 4);
                            }
                            var gd = await strokes.fromRgba(gFrame, pw, ph);
                            sub(i);                        // detector ran on the plate
                            if (gd) {
                                var gh = detHitsIn(gd, gBbox, pw, ph);
                                if (gh.hits > Math.max(30, gh.area * 0.004)) {
                                    kind = 'permanent';
                                    meta.kind = 'permanent';
                                    meta.ghostPlate = true;
                                }
                            }
                        } catch (e) { /* keep intermittent */ }
                        sub(i);                                    // ghost-plate check
                    }
                    if (kind === 'intermittent' && reply.usable) {
                        fire({ type: 'setPlateUsable', regionId: r.id, usable: true });
                        fire({ type: 'setRegionMeta', regionId: r.id, kindResolved: 'intermittent' });
                        // one-shot network completion of the plate's unknown pixels
                        if (netFill && reply.lowFrac > 0.02) {
                            try {
                                var bboxI = bboxProcOf(r);
                                var upI = await netOverFrame(bboxI, new Uint8ClampedArray(reply.plate), new Uint8Array(reply.bgLow), midT(r));
                                if (upI) { await call({ type: 'plateComplete', regionId: r.id, plate: upI.buffer }, [upI.buffer]); meta.net = true; }
                                sub(i);                            // plate completed
                            } catch (e) { /* Telea floor stays */ }
                        }
                    } else if (kind === 'permanent' && reply.usable) {
                        var bbox = bboxProcOf(r);
                        // ── Tier 3: translucent overlay → per-frame algebraic
                        // unblend. The α map came free with the plate build; a
                        // candidate region must (a) carry meaningful α mass,
                        // (b) sit on a background with temporal dynamics (else
                        // the estimate is fiction), and (c) pass the detector:
                        // we unblend the mid-clip frame and DBNet must see no
                        // text in the result. Any doubt → the patch tiers.
                        var translucent = false;
                        if (strokes && strokes.fromRgba) {
                            try {
                                var padB = padBoxes.get(r.id);
                                var pu2 = padUs.get(r.id);
                                var padSlices = perRegionPad.get(r.id) || [];
                                var ubReply = await call({
                                    type: 'unblendBuild', regionId: r.id,
                                    bbox: padB, uw: pu2.uw, uh: pu2.uh, inner: pu2.inner,
                                    slices: padSlices.map(function (sl) { return sl.buffer; })
                                }, padSlices.map(function (sl) { return sl.buffer; }));
                                sub(i);                            // unblend model built
                                meta.ubMass = ubReply.mass;
                                meta.ubDecidable = ubReply.decidable;
                                meta.ubColorDev = ubReply.colorDev;
                                // coloured overlays are NOT invertible yet (the
                                // luma-driven α doesn't converge; measured WORSE
                                // than untouched) — colorDev flags them and the
                                // patch tiers keep the region
                                if (ubReply.mass > 0.01 && ubReply.mass < 0.5 && ubReply.decidable > 0.5 && ubReply.colorDev <= 60) {
                                    // verify at NATIVE res: unblend the mid-clip crop and
                                    // the detector must see no text in the composite
                                    await procFrameAt(midT(r));
                                    pu2.ctx.drawImage(seekVideo, pu2.nx, pu2.ny, pu2.nw, pu2.nh, 0, 0, pu2.uw, pu2.uh);
                                    var uSlice = new Uint8ClampedArray(pu2.ctx.getImageData(0, 0, pu2.uw, pu2.uh).data);
                                    var ur = await call({ type: 'translucentApply', regionId: r.id, slice: uSlice.buffer }, [uSlice.buffer]);
                                    var nvC = makeCanvas(vw, vh), nvX = ctxOf(nvC);
                                    nvX.drawImage(seekVideo, 0, 0, vw, vh);
                                    var unC = makeCanvas(pu2.uw, pu2.uh);
                                    ctxOf(unC).putImageData(new ImageData(new Uint8ClampedArray(ur.rgba), pu2.uw, pu2.uh), 0, 0);
                                    nvX.imageSmoothingEnabled = true; nvX.imageSmoothingQuality = 'high';
                                    nvX.drawImage(unC, 0, 0, pu2.uw, pu2.uh, pu2.nx, pu2.ny, pu2.nw, pu2.nh);
                                    var uv = await strokes.fromRgba(nvX.getImageData(0, 0, vw, vh).data, vw, vh);
                                    if (uv) {
                                        var uhh = detHitsIn(uv, { x: bbox.x / pw * vw, y: bbox.y / ph * vh, w: bbox.w / pw * vw, h: bbox.h / ph * vh }, vw, vh);
                                        if (uhh.hits <= Math.max(30, uhh.area * 0.004)) translucent = true;
                                    }
                                }
                            } catch (e) { /* patch tiers */ }
                        }
                        if (translucent) {
                            fire({ type: 'setRegionMeta', regionId: r.id, kindResolved: 'translucent' });
                            meta.kind = 'translucent';
                            meta.unblend = true;
                            regionMeta.set(r.id, meta);
                            onProgress(0.7 + 0.3 * (i + 1) / plateTargets.length);
                            continue;
                        }
                        fire({ type: 'setRegionMeta', regionId: r.id, kindResolved: 'permanent' });
                        // one-shot patch: median appearance + core inpaint (+ network fill),
                        // with the core shrunk to detected glyph strokes when possible
                        var strokeSmall = null;
                        sub(i);                                    // tier decided
                        var det = await strokesDet(midT(r));
                        sub(i);                            // glyph strokes located
                        if (det) strokeSmall = strokeCore(det, bbox, new Uint8ClampedArray(reply.plate), pw, ph);
                        if (strokeSmall) {
                            // translucent-plaque guard needs the real footage ring
                            var tintFrame = await procFrameAt(midT(r));
                            sub(i);                        // plaque guard
                            if (plaqueTint(tintFrame, bbox, strokeSmall, pw, ph)) {
                                strokeSmall = null;
                                meta.plaque = true;
                            }
                        }
                        if (strokeSmall) meta.strokeCore = true;
                        var job = await buildPermanentJob(r, bbox, reply, strokeSmall);
                        sub(i);                            // fill job prepared
                        var pr = await call({
                            type: 'permanentPatch', regionId: r.id, x: job.x, y: job.y, w: job.w, h: job.h,
                            slice: job.rgba.buffer, core: job.core.buffer
                        }, [job.rgba.buffer, job.core.buffer]);
                        sub(i);                                    // fill computed
                        meta.degraded = !!pr.degraded;
                        var finalRgba = new Uint8ClampedArray(pr.rgba);
                        if (netFill) {
                            try {
                                var upP = await netOverFrame(job, new Uint8ClampedArray(pr.rgba), job.coreForNet(), midT(r));
                                if (upP) {
                                    finalRgba = new Uint8ClampedArray(upP);
                                    await call({ type: 'patchOverride', regionId: r.id, rgba: upP.buffer }, [upP.buffer]);
                                    meta.net = true;
                                }
                            } catch (e) { }
                        }
                        if (strokeSmall) pendingVerify.push({ r: r, reply: reply, bbox: bbox, job: job, rgba: finalRgba });
                    } else {
                        fire({ type: 'setRegionMeta', regionId: r.id, forceWindow: true });
                        meta.window = true;
                    }
                    regionMeta.set(r.id, meta);
                    onProgress(0.7 + 0.3 * (i + 1) / plateTargets.length);
                }
            }
            /* ── post-fill verification of stroke-core regions ──
               Compose the regions' own frame at NATIVE res with the fills applied
               (exactly what the viewer will see) and ask the detector again.
               Text still visible inside a region ⇒ the stroke mask missed part
               of the overlay — rebuild that region with the proven full-rect
               core. Best-effort: any failure here keeps the stroke fills. */
            // one composite per moment: regions of different shots are verified on
            // frames of their own shots (a single mid-clip frame showed none of them)
            var verifyAt = {};
            pendingVerify.forEach(function (pv0) { var key = midT(pv0.r).toFixed(2); (verifyAt[key] = verifyAt[key] || []).push(pv0); });
            for (var vkey in verifyAt) if (verifyAt.hasOwnProperty(vkey) && strokes && strokes.fromRgba) {
                var pvList = verifyAt[vkey];
                try {
                    await procFrameAt(+vkey);              // positions seekVideo at the regions' moment
                    var nc = makeCanvas(vw, vh), nx = ctxOf(nc);
                    nx.drawImage(seekVideo, 0, 0, vw, vh);
                    var vsx = vw / pw, vsy = vh / ph;
                    nx.imageSmoothingEnabled = true; nx.imageSmoothingQuality = 'high';
                    for (var vi = 0; vi < pvList.length; vi++) {
                        var pv = pvList[vi];
                        var pcv = makeCanvas(pv.job.w, pv.job.h);
                        ctxOf(pcv).putImageData(new ImageData(pv.rgba, pv.job.w, pv.job.h), 0, 0);
                        nx.drawImage(pcv, 0, 0, pv.job.w, pv.job.h, pv.job.x * vsx, pv.job.y * vsy, pv.job.w * vsx, pv.job.h * vsy);
                    }
                    var vd = await strokes.fromRgba(nx.getImageData(0, 0, vw, vh).data, vw, vh);
                    if (vd) {
                        for (var vj = 0; vj < pvList.length; vj++) {
                            var pv2 = pvList[vj];
                            var bx = pv2.bbox;
                            // vd is over the NATIVE frame — same normalized mapping
                            var vh2 = detHitsIn(vd, { x: bx.x / pw * vw, y: bx.y / ph * vh, w: bx.w / pw * vw, h: bx.h / ph * vh }, vw, vh);
                            if (vh2.hits > Math.max(30, vh2.area * 0.004)) {
                                var job2 = await buildPermanentJob(pv2.r, bx, pv2.reply);
                                var prv = await call({
                                    type: 'permanentPatch', regionId: pv2.r.id, x: job2.x, y: job2.y, w: job2.w, h: job2.h,
                                    slice: job2.rgba.buffer, core: job2.core.buffer
                                }, [job2.rgba.buffer, job2.core.buffer]);
                                var m2 = regionMeta.get(pv2.r.id);
                                if (m2) { m2.strokeCore = false; m2.strokeReverted = true; m2.degraded = !!prv.degraded; }
                                if (netFill) {
                                    try {
                                        var upR = await netOverFrame(job2, new Uint8ClampedArray(prv.rgba), job2.coreForNet(), midT(pv2.r));
                                        if (upR) { await call({ type: 'patchOverride', regionId: pv2.r.id, rgba: upR.buffer }, [upR.buffer]); if (m2) m2.net = true; }
                                    } catch (e) { }
                                }
                            }
                        }
                    }
                } catch (e) { /* verification is best-effort */ }
            }

            for (var kfi = 0; kfi < v2Recon.length; kfi++) {
                var kr = v2Recon[kfi];
                if (kr.keyframes && kr.keyframes.length) {
                    fire({ type: 'setRegionMeta', regionId: kr.id, forceWindow: true });
                    regionMeta.set(kr.id, { kind: 'moving', window: true });
                }
            }
            analyzed = true;
            analyzedSig = regionSig(userRegions);
            onProgress(1);
            return metaOut();
        }

        /* composite worker patches (proc-res) onto a full-res ctx */
        function compositePatches(octx, patches, ow, oh) {
            if (!patches || !patches.length) return;
            var sx = ow / pw, sy = oh / ph;
            for (var i = 0; i < patches.length; i++) {
                var pt = patches[i];
                // Worker-boundary validation: a patch whose dims don't agree
                // with its own buffers is corrupt (OOM-truncated transfer on
                // low-RAM tablets) — constructing ImageData from it threw
                // IndexSizeError "exceeds the supported range" and killed the
                // WHOLE composite (TIMBRICA-FRONTEND-D1). Skip just the bad
                // patch: one region degrades for one frame, the rest survive.
                if (!pt || !(pt.w >= 1) || !(pt.h >= 1) ||
                    !pt.rgba || pt.rgba.byteLength !== pt.w * pt.h * 4 ||
                    !pt.alpha || pt.alpha.byteLength !== pt.w * pt.h * 4) {
                    try { if (window.__ae) window.__ae('tool_error', { slug: 'remove-text-from-video', error_type: 'composite_bad_patch', w: pt && pt.w, h: pt && pt.h }); } catch (_) {}
                    continue;
                }
                var rgba = new Uint8ClampedArray(pt.rgba);
                var alpha = new Float32Array(pt.alpha);
                var img = new ImageData(pt.w, pt.h);
                var d = img.data;
                for (var j = 0, p = 0; j < alpha.length; j++, p += 4) {
                    d[p] = rgba[p]; d[p + 1] = rgba[p + 1]; d[p + 2] = rgba[p + 2];
                    d[p + 3] = Math.round(Math.max(0, Math.min(1, alpha[j])) * 255);
                }
                var pc = makeCanvas(pt.w, pt.h);
                ctxOf(pc).putImageData(img, 0, 0);
                // v3 (layer engine) patches are already at the frame's own grid —
                // drawn 1:1, never stretched from the 480-px processing grid
                var psx = pt.full ? ow / (pt.gridW || ow) : sx, psy = pt.full ? oh / (pt.gridH || oh) : sy;
                octx.save();
                octx.imageSmoothingEnabled = true; octx.imageSmoothingQuality = 'high';
                octx.drawImage(pc, 0, 0, pt.w, pt.h, pt.x * psx, pt.y * psy, pt.w * psx, pt.h * psy);
                octx.restore();
            }
        }

        /* ── COVERAGE PROBE: what will the render actually be able to rebuild? ──
         *
         * previewAt() feeds the worker a deliberately SPARSE pseudo-ring (seven
         * frames at ±1, ±0.25 s, ±0.7 s) because it only has to draw one frame.
         * That is fine for a picture and useless as a measurement: a moving
         * object still overlaps itself across those few samples, so the window
         * tier finds fewer than its minimum two candidates and reports 0%
         * coverage on footage that renders at 100%. Estimating from it would
         * make the tool's one honest number the least honest thing on the page.
         *
         * So the probe builds the same DENSE window the export will have — just
         * at a handful of times instead of every frame — and reads the coverage
         * the engine reports for those. Cost is a few seconds; the alternative
         * is a two-minute render to find out.
         */
        /* ── DENSE WINDOW: frames kFrom..kTo at processing resolution ──────────
         *
         * One WebCodecs RANGE pass through VbrFrameSource — the same source the
         * export reads, so the probe and the preview see the frames the render
         * will see, index for index — with a per-frame <video> seek as the
         * floor when WebCodecs cannot open the file.
         *
         * Until 2026-09-13 both callers seeked on purpose: the extractor's range
         * mode never noticed the end of a range and died in its flush watchdog
         * (~15 s per 0.8-s window), so seeks were the faster route. With the
         * range fixed and a keyframe seek in front of it, a 25-frame window of a
         * 1080×1920@60 phone clip measured 0.9–1.1 s at 0:40 (Chrome 152, hardware
         * decode) against 14–15 s linear, frames bit-identical (75/75); prod's
         * probe on seeks ran p50 17 s, the dense preview p50 9 s. */
        async function windowFrames(kFrom, kTo, onEach, signal) {
            kFrom = Math.max(0, kFrom);
            kTo = Math.min(kTo, lastK());
            if (kTo < kFrom) return 'empty';
            var want = kTo - kFrom + 1, got = 0, seen = {};
            if (root.VbrFrameSource && file && !root.__RT_WINDOW_SEEKS) {
                try {
                    var wopts = { file: file, url: url, fps: fps, width: pw, height: ph, abortSignal: signal };
                    if (TL) wopts.times = TL.times.slice(kFrom, kTo + 1);
                    else { wopts.trimStart = kFrom / fps; wopts.trimEnd = (kTo + 1) / fps; }
                    await root.VbrFrameSource.iterate(wopts, async function (canvas, i) {
                        var k = kFrom + i;
                        if (k > kTo || seen[k]) return;
                        seen[k] = 1;
                        await onEach(k, ctxOf(canvas).getImageData(0, 0, pw, ph).data);
                        got++;
                        beat();
                    });
                    if (got >= want) return 'webcodecs';
                } catch (e) {
                    if ((signal && signal.aborted) || (e && e.name === 'AbortError')) throw e;
                    try { root.__aeFeature && root.__aeFeature('rt_window_decode_fallback', { msg: String(e && e.message || e).slice(0, 80) }); } catch (_) {}
                }
            }
            for (var k = kFrom; k <= kTo; k++) {
                if (seen[k]) continue;
                if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
                await onEach(k, await procFrameAt(seekTimeOf(k)));
            }
            return got ? 'mixed' : 'seek';
        }
        function pushFrame(k, data) {
            var luma = lumaFrom(data, pw * ph);
            var rgba = new Uint8ClampedArray(data);
            fire({ type: 'push', k: k, rgba: rgba.buffer, luma: luma.buffer }, [rgba.buffer, luma.buffer]);
        }

        async function probeCoverage(times, popts) {
            popts = popts || {};
            var signal = popts.signal || null;
            if (!analyzed) await analyze({});
            fire({ type: 'resetCoverage' });
            lastCoverage = null;
            var W = (vw * vh > 1920 * 1080 ? 8 : 12);
            // Stride 1: the probe must sample the window exactly as densely as
            // the render will, or its answer is biased low and the tool
            // under-promises. Measured on the bench fixture: stride 2 reported
            // 67% where the render achieved 100%.
            var methods = {};
            for (var ti = 0; ti < times.length; ti++) {
                if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
                var c = kAt(times[ti]);
                var how = await windowFrames(c - W, c + W, function (k, data) { pushFrame(k, data); }, signal);
                methods[how] = (methods[how] || 0) + 1;
                if (signal && signal.aborted) {
                    for (var ka = c - W; ka <= c + W; ka++) fire({ type: 'evict', k: ka });
                    throw new DOMException('aborted', 'AbortError');
                }
                await new Promise(function (resolve) {
                    emitWaiters.set(c, resolve);
                    fire({ type: 'emit', c: c, t: tOf(c), lastIndex: Math.min(lastK(), c + W), probe: true });
                });
                for (var kk = c - W; kk <= c + W; kk++) fire({ type: 'evict', k: kk });
                if (popts.onProgress) popts.onProgress((ti + 1) / times.length, lastCoverage);
            }
            probeMethods = methods;
            return lastCoverage;
        }
        var probeMethods = null;

        /* ── PREVIEW: same math as export, on one frame ── */
        async function previewAt(t, targetCtx, ow, oh, popts) {
            if (!analyzed) await analyze({});
            var c = kAt(t);
            // Sparse ring (default): seven spread samples, enough to draw a
            // screen-fixed overlay's frame cheaply. DENSE: the same contiguous
            // window the render will have — required for a MOVING object,
            // whose neighbours at ±1 frame still overlap it. Previewing a
            // moving object off the sparse ring shows a WORSE frame than the
            // export will produce, which is a lie in the pessimistic direction
            // and would talk users out of a good result.
            var W = (vw * vh > 1920 * 1080 ? 8 : 12);
            if (popts && popts.dense) {
                // One range pass (see windowFrames) instead of 2W+1 seeks.
                try {
                    await windowFrames(c - W, c + W, function (k, data) { pushFrame(k, data); }, popts.signal || null);
                } catch (e) {
                    // A cancelled preview must not leave half a window in the ring.
                    for (var ka = c - W; ka <= c + W; ka++) fire({ type: 'evict', k: ka });
                    throw e;
                }
            } else {
                var offs = [0, -1, 1, -Math.round(0.25 * fps), Math.round(0.25 * fps), -Math.round(0.7 * fps), Math.round(0.7 * fps)];
                var seen = {};
                for (var i = 0; i < offs.length; i++) {
                    var k = c + offs[i];
                    if (k < 0 || k > lastK() || seen[k]) continue;
                    seen[k] = 1;
                    pushFrame(k, await procFrameAt(seekTimeOf(k)));
                }
            }
            var patches = await new Promise(function (resolve) {
                emitWaiters.set(c, resolve);
                fire({ type: 'emit', c: c, t: t, lastIndex: Math.min(lastK(), c + Math.round(0.7 * fps)) });
            });
            // A dense window is 2W+1 frames of RGBA plus float luma (~40 MB at
            // 480p): let it go once the frame is drawn, as the probe does. The
            // export pushes its own frames.
            if (popts && popts.dense) {
                for (var ke = c - W; ke <= c + W; ke++) fire({ type: 'evict', k: ke });
            }
            // v3 layer regions: rebuilt on the full-resolution frame by the same
            // engine the export runs, returned as source-grid patches
            if (ovEng && hasOv()) {
                var sv = await frameSurfaceAt(t);
                var fc = makeCanvas(vw, vh), fcx = ctxOf(fc);
                fcx.drawImage(sv, 0, 0, vw, vh);
                await ovEng.prepareRender(vw, vh);
                var lp = await ovEng.composite(fcx, c, t, vw, vh, { protects: protectRegions, returnPatches: true });
                if (lp && lp.length) patches = (patches || []).concat(lp);
            }
            if (targetCtx) {
                compositePatches(targetCtx, patches, ow, oh);
                for (var ci = 0; ci < censorRegions.length; ci++) {
                    var cr = censorRegions[ci];
                    if (t >= (cr.startTime || 0) && t <= (cr.endTime == null ? dur : cr.endTime)) applyCensor(targetCtx, cr, ow, oh, protectRegions);
                }
            }
            return patches;
        }

        /* ── EXPORT ── */
        /* One watchdog over the WHOLE export — analyze, stream and encode.
           There are four places this pipeline waits without a deadline
           (bridge.take, the encoder's drain spin, drawFrame, and the worker
           round-trip in call()), and a wedge in any of them looked identical to
           the user: frozen bar, no error, forever. Guarding the parts separately
           would need four thresholds and would still miss whichever one nobody
           thought of; guarding the whole span with a single "has ANYTHING moved"
           tick needs one, and it covers waits that do not exist yet.
           `beat()` is called from every real unit of work, never from a wait. */
        async function exportRun(eopts) {
            eopts = eopts || {};
            if (typeof root.__withStallGuard !== 'function') return exportRunInner(eopts);
            return root.__withStallGuard(function () { return exportRunInner(eopts); }, {
                stage: 'export', adaptive: true,
                floorMs: stallFloorMs(), hardMs: stallFloorMs(),
                softMs: Math.round(stallFloorMs() * 0.5),   // warn before killing, at any scale
                meta: { slug: (root.__TOOL_SLUG || ''), lane: 'recon-pipeline' },
                // Abort the caller's controller: that unwinds producer, worker
                // ring and encoder together. The default (FFmpegProcessor.cancel)
                // would have shot an engine this lane is not even using.
                cancel: function () { try { eopts.onStallAbort && eopts.onStallAbort(); } catch (_) {} }
            });
        }
        /* How much of the EXPORT'S WORK is behind us at frame k.
           A frame the objects are not on costs a copy; a frame they are on costs
           a reconstruction, and the two differ by more than an order of
           magnitude. Counting frames therefore made the bar crawl through the
           object's span and sprint through the rest — measured on the dogwalk
           fixture (objects on 3.6 s of 8): 10 % took 1.1 s, 10→50 % took 104 s,
           50→100 % took 20 s. The bar was honest about frames and dishonest
           about time, and the remaining-time line inherited that: its first
           estimate came out 2.4× short on every clip.
           IDLE_COST is the cost of a copied frame relative to a reconstructed
           one — the ratio those same measurements imply (~1.1 s for 24 frames
           against ~104 s for 108). */
        var IDLE_COST = 0.04;
        /* `perFrame` is work EVERY frame carries on top of that — a postFrame
           stage that runs on the whole clip (a matting net per frame), in the
           same units as a reconstructed frame. 0 keeps the old weighting. */
        function makeWorkShare(regions, fps, total, perFrame, tl) {
            var extra = Math.max(0, +perFrame || 0);
            var busy = new Uint8Array(Math.max(1, total));
            (regions || []).forEach(function (r) {
                var a = Math.max(0, tl ? tl.firstFrom(r.startTime || 0) : Math.floor((r.startTime || 0) * fps));
                var b = Math.min(total - 1, tl ? (r.endTime == null ? total - 1 : tl.onScreen(r.endTime)) : Math.ceil((r.endTime == null ? total / fps : r.endTime) * fps));
                for (var i = a; i <= b; i++) busy[i] = 1;
            });
            var cum = new Float64Array(total + 1), acc = 0;
            for (var k = 0; k < total; k++) { acc += (busy[k] ? 1 : IDLE_COST) + extra; cum[k + 1] = acc; }
            var all = acc || 1;
            return function (k) {
                var i = Math.max(0, Math.min(total, k + 1));
                return cum[i] / all;
            };
        }

        async function exportRunInner(eopts) {
            eopts = eopts || {};
            if (!analyzed) {
                if (eopts.onStage) eopts.onStage('analyze');
                await analyze({
                    onProgress: function (f) { if (eopts.onProgress) eopts.onProgress(0.12 * f); },
                    signal: eopts.signal
                });
            }
            if (eopts.signal && eopts.signal.aborted) {
                throw new DOMException('aborted', 'AbortError');
            }
            var signal = eopts.signal;
            // Cancellation has to be checked between the SETUP awaits too, not
            // only inside the frame loop. Measured before this: clicking cancel
            // while the export was still opening the file and decoding audio
            // left the page busy for 39-45 s on an 8-second clip, because the
            // first signal check lived in the producer callback that had not
            // started yet.
            function abortCheck() {
                if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
            }
            abortCheck();
            // The delivered coverage must describe THIS render: without a reset
            // it accumulated the probe's frames, the preview's and a cancelled
            // run's — the e2e's cancel-after-4-s step double-counted the clip's
            // first hundred frames (the object entering at the edge, ~0%) and
            // reported 31% for a render whose frames averaged 55%.
            fire({ type: 'resetCoverage' });
            lastCoverage = null;
            var ow = eopts.outWidth || vw, oh = eopts.outHeight || vh;
            var N = TL ? TL.n : Math.max(1, Math.round(dur * fps));
            // v3 regions: world regions at the OUTPUT size + the never-seen synthesis
            if (ovEng && hasOv()) {
                await ovEng.prepareRender(ow, oh);
                abortCheck();
                // A region v3 claimed at analyze() but could not prepare must not be
                // left untouched: it goes back to the previous engine (re-analysis).
                var lostIds = Object.keys(ovIds).filter(function (id) { return !ovEng.handles(id); });
                if (lostIds.length) {
                    lostIds.forEach(function (id) { ovEng.forceV2(id, 'prepare_failed'); });
                    try { root.__aeFeature && root.__aeFeature('rt_ov_prepare_failed', { n: lostIds.length }); } catch (_) {}
                    analyzed = false;
                    await analyze({ signal: signal });
                    abortCheck();
                    if (hasOv()) await ovEng.prepareRender(ow, oh);
                }
            }
            // The worker ring only serves the previous engine's regions: with none
            // left there is nothing to look ahead for, and holding ~0.7 s of
            // full-resolution frames would only cost memory.
            var v2ReconLeft = reconRegions.some(function (r) { return !ovIds[r.id]; });
            var ringR = v2ReconLeft ? Math.max((vw * vh > 1920 * 1080 ? 8 : 12), Math.ceil(0.7 * fps)) : 0;
            var heavy = ow * oh > 1920 * 1080;          // store outs compressed above 1080p
            var outs = new Map();                        // k → canvas | Promise<Blob(webp)>
            var bridge = makeBridge(8);
            var lastEmitted = -1, lastTailFrame = null, producerDone = false;

            var audioBuffer = null;
            if (!eopts.mute) {
                try {
                    var ab = await file.arrayBuffer();
                    abortCheck();
                    var AC = window.AudioContext || window.webkitAudioContext;
                    if (AC) { var actx = new AC(); audioBuffer = await actx.decodeAudioData(ab.slice(0)); actx.close && actx.close(); }
                } catch (e) { audioBuffer = null; }
            }
            abortCheck();
            if (eopts.onStage) eopts.onStage('reconstruct');

            /* Full-res frames above 1080p are parked as compressed blobs so the
               ring of frames waiting to emit does not sit in the heap raw. The
               encoder is not a given: WebKit answers EncodingError from
               convertToBlob on an iPhone under memory pressure (Sentry JE, iOS
               18.7, 4K source), and a promise stored in `outs` with no handler
               attached fires unhandledrejection the moment it fails — long
               before emitFrame() awaits it. So the fallback is attached HERE,
               synchronously: WebP → JPEG → the canvas itself. A frame that could
               not be compressed costs memory, never the export. */
            function compressOut(cnv) {
                return cnv.convertToBlob({ type: 'image/webp', quality: 0.96 })
                    .catch(function () { return cnv.convertToBlob({ type: 'image/jpeg', quality: 0.95 }); })
                    .catch(function () {
                        try { root.__aeFeature && root.__aeFeature('rt_out_compress_failed', {}); } catch (_) {}
                        return cnv;
                    });
            }
            async function outToCanvas(v) {
                if (v && typeof v.then === 'function') v = await v;
                if (v instanceof Blob) {
                    var bmp = await createImageBitmap(v);
                    var cnv = makeCanvas(ow, oh);
                    ctxOf(cnv).drawImage(bmp, 0, 0);
                    bmp.close && bmp.close();
                    return cnv;
                }
                return v;
            }
            async function emitFrame(c, lastIndex) {
                // ⚠️ Записи может НЕ БЫТЬ — и это не авария, а повторный заход.
                //
                // Источник кадров при отказе WebCodecs ВОЗОБНОВЛЯЕТСЯ с кадра
                // `progress.emitted` (frame-source.js: «must never restart at 0»), то есть
                // тот же исходный кадр приезжает второй раз — и снова зовёт
                // emitFrame(k - ringR). Если в первой попытке мы успели удалить
                // запись и упали позже, второй заход получал undefined и падал
                // на ctxOf(). Замер прода (7 дней): 129 событий / 85 сессий
                // «Cannot read properties of undefined (reading 'getContext')» — место
                // найдено по стеку, который добавили в toast_error 23.08 ровно ради
                // этого случая.
                //
                // Почему пропуск БЕЗОПАСЕН именно С ПЕРЕНОСОМ удаления ниже:
                // запись теперь исчезает ТОЛЬКО после успешного bridge.put, значит
                // «записи нет» строго равносильно «кадр уже отдан кодировщику».
                // Просто «пропускать при undefined» было бы НЕВЕРНО: если падение
                // случилось ДО put, кадр никуда не ушёл, и пропуск оставил бы дыру,
                // от которой кодировщик ждёт вечно — тот самый «экспорт замёрз навсегда»,
                // что чинили 29.07 (см. комментарий в frame-source.js).
                //
                // Удержание записи до put не стоит памяти: в мапе лежит ТОТ ЖЕ
                // объект, что уходит в bridge, а не копия.
                if (!outs.has(c)) {
                    try { root.__aeFeature && root.__aeFeature('rt_emit_reentry_skipped', { c: c }); } catch (_) {}
                    return;
                }
                var t = tOf(c);
                var patchesProm = new Promise(function (resolve) {
                    emitWaiters.set(c, resolve);
                    fire({ type: 'emit', c: c, t: t, lastIndex: lastIndex });
                });
                var out = await outToCanvas(outs.get(c));
                var octx = ctxOf(out);
                var patches = await patchesProm;
                compositePatches(octx, patches, ow, oh);
                if (ovEng && hasOv()) await ovEng.composite(octx, c, t, ow, oh, { protects: protectRegions });
                for (var ci = 0; ci < censorRegions.length; ci++) {
                    var cr = censorRegions[ci];
                    if (t >= (cr.startTime || 0) && t <= (cr.endTime == null ? dur : cr.endTime)) applyCensor(octx, cr, ow, oh, protectRegions);
                }
                /* The page's last word on the finished frame, IN ORDER (c is
                   strictly increasing here — a recurrent model may rely on it):
                   /remove-object-from-video puts the people on a new background
                   (BgReplace) after the marked objects are rebuilt, so the new
                   background never shows a removed object and a blur blurs the
                   cleaned frame. A returned canvas replaces the frame; a throw
                   fails the export like any other. */
                if (eopts.postFrame) {
                    var post = await eopts.postFrame(out, c, t);
                    if (post) out = post;
                    beat();
                }
                if (eopts.onFramePreview && (c % Math.max(1, Math.round(fps / 2)) === 0)) {
                    try { eopts.onFramePreview(out, c, N, t); } catch (e) { }
                }
                fire({ type: 'evict', k: c - ringR - 1 });
                lastEmitted = c;
                lastTailFrame = out;      // kept for the tail-padding contract below
                beat();
                await bridge.put(c, out);
                // Удаляем ТОЛЬКО после успешной передачи — это и делает
                // проверку `outs.has(c)` выше достоверным признаком «уже отдано».
                outs.delete(c);
            }

            var workShare = makeWorkShare(regions, fps, N, eopts.perFrameCost, TL);   // progress by work, see makeWorkShare
            var popts = { file: file, url: url, fps: fps, trimStart: 0, trimEnd: dur, width: ow, height: oh, abortSignal: signal };
            if (TL) popts.times = TL.times;
            var producer = root.VbrFrameSource.iterate(popts, async function (frameCanvas, k, t) {
                if (signal && signal.aborted) throw new DOMException('aborted', 'AbortError');
                // full-res out copy (compressed store above 1080p bounds memory:
                // the ring spans ~2×0.7 s of full-res frames waiting to emit)
                var out = makeCanvas(ow, oh);
                ctxOf(out).drawImage(frameCanvas, 0, 0, ow, oh);
                // parked compressed only while it waits in the look-ahead ring; with
                // no ring (v3-only sessions) a WebP round trip would just cost quality
                outs.set(k, (heavy && ringR > 0 && out.convertToBlob) ? compressOut(out) : out);
                // proc-res copy for the worker ring
                var pc = makeCanvas(pw, ph), pcx = ctxOf(pc);
                pcx.drawImage(frameCanvas, 0, 0, pw, ph);
                var pd = pcx.getImageData(0, 0, pw, ph).data;
                var luma = lumaFrom(pd, pw * ph);
                var rgba = new Uint8ClampedArray(pd);
                fire({ type: 'push', k: k, rgba: rgba.buffer, luma: luma.buffer }, [rgba.buffer, luma.buffer]);
                var c = k - ringR;
                if (c >= 0) await emitFrame(c, k);
                if (eopts.onProgress) eopts.onProgress(0.12 + 0.78 * workShare(k));
            });

            // mp4/webm muxers demand an INTEGER frame rate; 29.97→30 shifts the
            // video track by 0.1% (imperceptible; audio keeps true duration)
            var encodeProm = root.CanvasVideoExport.encode({
                width: ow, height: oh, fps: Math.max(1, Math.round(fps)), durationSec: dur, format: eopts.format || 'mp4',
                // the file keeps the source's own frame times (see TL above)
                frameTimes: TL ? TL.times : undefined,
                qualityCrf: eopts.qualityCrf || 23, audioBuffer: audioBuffer, signal: signal,
                // Frames arrive once, through a one-shot bridge; the encoder's
                // hint cascade must not ask for one twice (see canvas-video-export.js).
                canReplayFrames: false,
                // exportRun already wraps analyze+stream+encode in ONE guard, so
                // the engine's own guard would be a second watchdog on the same
                // ticks with a second error path. The engine keeps its guard for
                // the 20 callers that do NOT wrap anything.
                noStallGuard: true,
                onStallAbort: function () { try { eopts.onStallAbort && eopts.onStallAbort(); } catch (_) {} },
                /* The encoder runs CONCURRENTLY with the producer, so its
                   fraction is not a tail — mapping it into 0.9…1.0 meant the
                   very first encoded frame reported 90%. That was invisible
                   only by luck: the producer's write for the same frame landed
                   in the same task and overwrote it before paint (verified by
                   rAF sampling, never a painted 90%). Luck is not a design, and
                   a caller that clamps its bar monotonic — the obviously correct
                   thing to do — turns the latent glitch into a bar stuck at 90%
                   for the whole run. The producer owns 0.12…0.90 while frames
                   stream; the encoder owns 0.90…1.0 for its flush-and-finalize
                   tail, which is genuinely the last thing that happens. */
                onProgress: function (f) {
                    if (!eopts.onProgress) return;
                    if (!producerDone) return;
                    eopts.onProgress(0.9 + 0.1 * Math.max(0, Math.min(1, f)));
                },
                drawFrame: async function (ctx, i) {
                    /* No stage flip here. `encode-start` fired on the encoder's
                       FIRST frame — milliseconds after streaming began — so the
                       label read "Кодирую…" for the entire reconstruction, the
                       slow part. Measured: "Восстанавливаю…" was on screen for
                       0.4 s of a 24.5 s export. The stage now flips where the
                       work actually changes hands (producer flush, above). */
                    var snap = await bridge.take(i);
                    ctx.drawImage(snap, 0, 0);
                }
            });

            // flush trailing centres once the producer finishes; a flush error
            // must reject the export (a silent one would deadlock the encoder)
            var flushed = producer.then(async function (res) {
                var last = (res && typeof res.frames === 'number') ? res.frames - 1
                    : (outs.size ? Math.max.apply(null, Array.from(outs.keys())) : lastEmitted);
                for (var c = lastEmitted + 1; c <= last; c++) {
                    if (outs.has(c)) await emitFrame(c, last);
                }
                /* ── the frame contract, CHECKED ──────────────────────────────
                   The encoder was told to expect round(dur × fps) frames and it
                   asks for every index in that range; bridge.take() has no
                   timeout, so one missing index is a permanent hang — frozen
                   bar, no error, nothing in telemetry. That is not theoretical:
                   iterateWebCodecs() pads its own rounding tail only `&& lastBitmap`,
                   so an extract that yields zero frames returns {frames:0}
                   perfectly happily. Injected on 2026-09-03 (producer resolves
                   at N/2): the page waited 75 s and would have waited forever.

                   The producer knows the truth the moment it resolves, so decide
                   HERE instead of waiting for a watchdog: repeat the last good
                   frame to fill the tail (what the source already does for its
                   own rounding), and if there is no good frame at all, say so.
                   A tail this long is a defect, not rounding — report it. */
                var want = TL ? TL.n : Math.max(1, Math.round(dur * Math.max(1, Math.round(fps))));
                if (lastEmitted + 1 < want) {
                    var short = want - (lastEmitted + 1);
                    if (lastEmitted < 0) throw new Error('frame-source-empty:0/' + want);
                    try { root.__aeFeature && root.__aeFeature('rt_frame_tail_padded', { got: lastEmitted + 1, want: want, method: res && res.method }); } catch (_) {}
                    var tail = await outToCanvas(lastTailFrame);
                    for (var p = lastEmitted + 1; p < want; p++) { beat(); await bridge.put(p, tail); }
                    lastEmitted = want - 1;
                    if (short > Math.max(2, Math.ceil(fps))) throw new Error('frame-source-short:' + (want - short) + '/' + want);
                }
                producerDone = true;      // from here the encoder's tail owns the bar
                if (eopts.onStage) eopts.onStage('encode');
            });

            var results = await Promise.all([encodeProm, flushed]);
            return results[0];
        }

        function dispose() {
            if (ovEng) { try { ovEng.dispose(); } catch (_) { } }
            if (worker) { worker.terminate(); worker = null; }
            pending.clear(); emitWaiters.clear();
            if (seekVideo && !seekByDecoder) {
                try { if (!url && seekVideo.src) URL.revokeObjectURL(seekVideo.src); } catch (e) { }
                seekVideo.removeAttribute('src'); seekVideo.load && seekVideo.load();
                if (root.VbrFrameSource && root.VbrFrameSource.detachHidden) root.VbrFrameSource.detachHidden(seekVideo);
                seekVideo = null;
            } else if (seekVideo) {
                seekVideo = null; seekSurfaceCtx = null;
            }
        }

        return {
            setRegions: setRegions,
            analyze: analyze,
            previewAt: previewAt,
            probeCoverage: probeCoverage,
            export: exportRun,
            compositePatches: compositePatches,
            meta: function () { return metaOut(); },
            // the cut list the engines used ([{t0, t1}], null before the first analyze)
            shots: function () { return shots; },
            // v3: per-region plans (verdict, why, layer stats, synthesis lane) and
            // the render's real / synthesised / floor pixel counts
            ovPlans: function () { return ovEng ? ovEng.plans() : null; },
            // the layer engine itself — the server fill attaches its band to a region there
            ovEngine: function () { return ovEng; },
            ovStats: function () { return ovEng ? ovEng.stats() : Promise.resolve([]); },
            coverage: function () { return lastCoverage; },
            // How the last probe got its frames: {webcodecs:n, seek:n, mixed:n}.
            probeMethods: function () { return probeMethods; },
            synthStats: function () { return call({ type: 'synthStats' }).then(function (r) { return r.stats; }).catch(function () { return null; }); },
            resetCoverage: function () { lastCoverage = null; if (worker) fire({ type: 'resetCoverage' }); },
            // Sparse proc-res frames ({rgba, t}) spanning the clip — the long-reach
            // sources of the object tier. Kept by the session, re-sent per init.
            setSamples: function (frames) {
                objectSamples = (Array.isArray(frames) && frames.length) ? frames : null;
                if (worker && analyzed) sendSamples();
            },
            dims: function () { return { pw: pw, ph: ph }; },
            dispose: dispose
        };
    }

    /* back-compat single-shot API */
    async function run(opts) {
        var session = createSession(opts);
        try {
            session.setRegions(opts.regions || []);
            await session.analyze({
                onProgress: function (f) { if (opts.onProgress) opts.onProgress(0.12 * f); },
                signal: opts.signal
            });
            return await session.export({
                format: opts.format, qualityCrf: opts.qualityCrf,
                outWidth: opts.outWidth, outHeight: opts.outHeight,
                mute: opts.mute, signal: opts.signal,
                onProgress: opts.onProgress, onStage: opts.onStage,
                onFramePreview: opts.onFramePreview
            });
        } finally {
            session.dispose();
        }
    }

    // _applyCensor — для стендов: то самое скрытие, которым экспорт закрывает
    // область (tests/playwright/redaction-blur-hides-on-every-engine.mjs).
    root.RtReconPipeline = { createSession: createSession, run: run, splitByShots: splitByShots, strokeCore: strokeCore, plaqueTint: plaqueTint, detHitsIn: detHitsIn, _applyCensor: applyCensor, __v2: true };
})(typeof window !== 'undefined' ? window : self);
